import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

declare global {
  interface Window { gtag?: (...args: unknown[]) => void; }
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

function getSessionId(): string {
  let id = sessionStorage.getItem('sf_session_id');
  if (!id) {
    id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('sf_session_id', id);
  }
  return id;
}

function sendTrack(payload: Record<string, unknown>) {
  const url = `${API_URL}/analytics/track`;
  const body = JSON.stringify(payload);

  if (navigator.sendBeacon) {
    navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
  } else {
    fetch(url, { method: 'POST', body, headers: { 'Content-Type': 'application/json' }, keepalive: true }).catch(() => {});
  }
}

export function useAnalytics() {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const mountTime = useRef(Date.now());
  const lastPath = useRef<string | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      const now = Date.now();

      // Send duration for previous page
      if (lastPath.current && lastPath.current !== location.pathname) {
        const duration = Math.round((now - mountTime.current) / 1000);
        if (duration > 0) {
          sendTrack({
            sessionId: getSessionId(),
            path: lastPath.current,
            duration,
            userId: user?.id || undefined,
          });
        }
      }

      // Track new page view
      sendTrack({
        sessionId: getSessionId(),
        path: location.pathname,
        referrer: document.referrer || undefined,
        screenWidth: window.innerWidth,
        userId: user?.id || undefined,
      });

      // Also send to Google Analytics (SPA route change)
      if (typeof window.gtag === 'function') {
        window.gtag('config', 'G-BP081Z5JYZ', { page_path: location.pathname });
      }

      lastPath.current = location.pathname;
      mountTime.current = now;
    }, 100);

    return () => clearTimeout(debounceTimer.current);
  }, [location.pathname, user?.id]);
}
